import { gtcVideoAutoplay, gtcThrottle, gtcDebounce } from "gtcUtils";

const POINTER_QUERY = window.matchMedia('(pointer: fine) and (any-hover: hover)');

class ProductCardImageCarousel extends HTMLElement {
    constructor() {
        super();

        this.IS_POINTER_DEVICE = POINTER_QUERY.matches;

        this.CONSTANTS = {
            SELECTORS: {
                CONTAINER: '.product-card-image-carousel__items',
                ITEMS_ON_TOUCH: '.product-card-image-carousel__item',
                ITEMS_ON_HOVER: '.product-card-image-carousel__item:not(.pointer\\:hidden)',
                BUTTONS: '.product-card-image-carousel__button',
                PROGRESS: '.product-card-image-carousel__progress',
            },
        };

        this.clickNavButton = this.clickNavButton.bind(this);
        this.handleScroll = this.handleScroll.bind(this);
        this.handleScrollEnd = this.handleScrollEnd.bind(this);
        this.pointerDeviceChange = this.pointerDeviceChange.bind(this);

        this.dom = {
            container: this.querySelector(this.CONSTANTS.SELECTORS.CONTAINER),
            items: this.querySelectorAll(this.IS_POINTER_DEVICE ? this.CONSTANTS.SELECTORS.ITEMS_ON_HOVER : this.CONSTANTS.SELECTORS.ITEMS_ON_TOUCH),
            buttons: this.querySelectorAll(this.CONSTANTS.SELECTORS.BUTTONS),
            progress: this.querySelector(this.CONSTANTS.SELECTORS.PROGRESS),
        };

        this.settings = {
            engaged: false,
            currentIndex: 0,
            minIndex: 0,
            maxIndex: this.dom.items.length - 1,
            scrollEndTimeout: null,
            hasPendingProgrammaticScroll: false,
            progressUpdateScheduled: false,
            throttledUpdateFromScroll: gtcThrottle(() => this.updateCurrentIndexFromScroll(), 100),
            touchSwipe: this.hasAttribute('data-touch-swipe') ? true : false,
        };

        this.handleResize = gtcDebounce(() => {
            if (this.settings.engaged) return;
            this.changeCurrentIndex(Number(this.settings.currentIndex || 0)); this.carouselScrollTo(Number(this.settings.currentIndex || 0));
        }, 150);
    }

    async connectedCallback() {
        if (this.classList.contains('is-single')) {
            return;
        }

        
        await Promise.all([
            this.setButtonStates(),
            this.addEventListeners(),
            this.updateCurrentIndexFromScroll(),
            this.updateProgressFromScroll()
        ]);
        
        this.classList.add('is-initialized');
    }

    disconnectedCallback() {
        if (this.classList.contains('is-single')) {
            return;
        }
        this.removeEventListeners();
    }

    addEventListeners() {
        this.dom?.buttons.forEach((button) => {
            button.addEventListener('click', this.clickNavButton);
        });
        window.addEventListener('resize', this.handleResize);

        POINTER_QUERY.addEventListener('change', this.pointerDeviceChange);

        this.dom?.container?.addEventListener('scroll', this.handleScroll);
        this.dom?.container?.addEventListener('scrollend', this.handleScrollEnd);
        if (this.settings.touchSwipe) {
            this.dom?.container?.addEventListener('touchmove', this.handleScroll, { passive: true });
            this.dom?.container?.addEventListener('touchend', this.handleScrollEnd);
        }
    }

    removeEventListeners() {
        this.settings.engaged = false;
        this.dom?.buttons?.forEach((button) => {
            button.removeEventListener('click', this.clickNavButton);
        });
        window.removeEventListener('resize', this.handleResize);
        clearTimeout(this.settings.scrollEndTimeout);

        POINTER_QUERY.removeEventListener('change', this.pointerDeviceChange);

        this.dom?.container?.removeEventListener('scroll', this.handleScroll);
        this.dom?.container?.removeEventListener('scrollend', this.handleScrollEnd);
        if (this.settings.touchSwipe) {
            this.dom?.container?.removeEventListener('touchmove', this.handleScroll);
            this.dom?.container?.removeEventListener('touchend', this.handleScrollEnd);
        }
    }

    pointerDeviceChange() {
        this.IS_POINTER_DEVICE = POINTER_QUERY.matches;
        requestAnimationFrame(() => {
            this.dom.items = this.querySelectorAll(this.IS_POINTER_DEVICE ? this.CONSTANTS.SELECTORS.ITEMS_ON_HOVER : this.CONSTANTS.SELECTORS.ITEMS_ON_TOUCH);
            this.settings.maxIndex = this.dom.items.length - 1;
        });
    }

    updateCurrentIndexFromScroll() {
        if (this.settings.hasPendingProgrammaticScroll) return;
        const index = Math.round(this.dom.container.scrollLeft / this.dom.items[0].offsetWidth);
        if (index < this.settings.minIndex) {
            this.settings.currentIndex = this.settings.minIndex;
        } else if (index > this.settings.maxIndex) {
            this.settings.currentIndex = this.settings.maxIndex;
        } else {
            this.settings.currentIndex = index;
        }


        this.changeCurrentIndex(this.settings.currentIndex);
    }

    updateProgressFromScroll() {
        const { container, progress } = this.dom;
        if (!container || !progress) return;
        const maxScroll = container.scrollWidth - container.clientWidth;
        if (maxScroll <= 0) return;
        const n = this.settings.maxIndex + 1;
        const percent = (1 + (container.scrollLeft / maxScroll) * (n - 1)) / n * 100;
        progress.style.setProperty('--progress', `${ percent }%`);
    }

    scheduleProgressUpdate() {
        if (this.settings.progressUpdateScheduled) return;
        this.settings.progressUpdateScheduled = true;
        requestAnimationFrame(() => {
            this.settings.progressUpdateScheduled = false;
            this.updateProgressFromScroll();
        });
    }

    handleScroll() {
        this.scheduleProgressUpdate();
        this.settings.throttledUpdateFromScroll();
        if (!('onscrollend' in window)) {
            clearTimeout(this.settings.scrollEndTimeout);
            this.settings.scrollEndTimeout = setTimeout(this.handleScrollEnd, 75);
        }
    }

    handleScrollEnd() {
        this.settings.hasPendingProgrammaticScroll = false;
        this.updateCurrentIndexFromScroll();
        this.updateProgressFromScroll();
    }

    changeCurrentIndex(index) {
        if (!this.settings.engaged) {
            this.classList.add('is-engaged');
        }
        this.settings.currentIndex = Number(index);

       this.setButtonStates();
    }

    clickNavButton(event) {
        const button = event.target;
        const isPrevButton = button.classList.contains('product-card-image-carousel__button--prev');
        const isNextButton = button.classList.contains('product-card-image-carousel__button--next');

        if (isPrevButton) {
            this.carouselScrollDirection(-1);
        } else if (isNextButton) {
            this.carouselScrollDirection(1);
        }
    }

    carouselScrollDirection(direction) {
        const newIndex = Math.max(this.settings.minIndex, Math.min(this.settings.maxIndex, this.settings.currentIndex + direction));
        this.carouselScrollTo(newIndex);
    }

    carouselScrollTo(index) {

        if (typeof index !== 'number' || isNaN(index)) {
            throw new Error('Index must be a number');
        }

        this.changeCurrentIndex(index);
        this.settings.hasPendingProgrammaticScroll = true;
        if (this.dom.items[this.settings.currentIndex]?.offsetLeft) {
        this.dom.container.scroll({ behavior: 'smooth', left: this.dom.items[this.settings.currentIndex].offsetLeft });
        } else {
            this.dom.container.scroll({ behavior: 'smooth', left: 0 });
        }
    }

    setButtonStates() {
        if (this.settings.currentIndex === this.settings.minIndex) {
            this.dom.buttons[0].classList.add('is-disabled');
        } else {
            this.dom.buttons[0].classList.remove('is-disabled');
        }

        if (this.settings.currentIndex === this.settings.maxIndex) {
            this.dom.buttons[1].classList.add('is-disabled');
        } else {
            this.dom.buttons[1].classList.remove('is-disabled');
        }
    }
}

customElements.define("product-card-image-carousel", ProductCardImageCarousel);

class ProductCardImageCarouselVideo extends gtcVideoAutoplay {
    constructor() {
        super();
    }
}

customElements.define("product-card-image-carousel-video", ProductCardImageCarouselVideo);
